import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CSS } from '@dnd-kit/utilities';
import { ScrollArea } from '@/components/ui/scroll-area.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import {
  TId,
  TUserWithAccessControl,
} from '@/features/plants/components/plant-management/user-access/plant-user-access-management.tsx';

export type TColumn = {
  id: TId;
  title: string;
};

const defaultCols: TColumn[] = [
  {
    id: 'available-users',
    title: 'Available users',
  },
  {
    id: 'associated-users',
    title: 'Associated users',
  },
];

function UserCard({ user }: { user: TUserWithAccessControl }) {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable(
    {
      id: user.userId,
      data: {
        type: 'User',
        user,
      },
    }
  );

  const style = {
    transition,
    transform: CSS.Transform.toString(transform),
  };

  if (isDragging) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="hover:ring-primary relative flex cursor-grab justify-between rounded-lg bg-slate-100 px-4 py-2 hover:ring-2 hover:ring-inset aria-disabled:opacity-50 aria-disabled:hover:ring-0"
      />
    );
  }

  const role = user.role.split(' ');
  const roleLabel =
    role.length === 1
      ? user.role
      : role.length === 2
        ? `${role?.[0]?.[0]?.toLocaleUpperCase()} ${role?.[1]?.[0]?.toLocaleUpperCase()}${role?.[1]?.slice(1)}`
        : '';

  return (
    <div
      title={`${user.firstName} ${user.lastName} - ${user.role}`}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="hover:ring-primary relative flex cursor-grab justify-between rounded-lg bg-slate-100 px-4 py-2 hover:ring-2 hover:ring-inset aria-disabled:opacity-50 aria-disabled:hover:ring-0"
    >
      <span className="my-auto w-full overflow-x-hidden overflow-y-auto whitespace-pre-wrap select-none">
        {user.firstName} {user.lastName} - {roleLabel}{' '}
        {user.newlyAdded && (
          <span className="inline-block size-2 rounded-full bg-blue-400"></span>
        )}
      </span>
      <button>
        <svg viewBox="0 0 20 20" width="12">
          <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
        </svg>
      </button>
    </div>
  );
}

function ColumnContainer({
  column,
  users,
}: {
  column: TColumn;
  users: TUserWithAccessControl[];
}) {
  const usersIds = useMemo(() => {
    return users.map((user) => user.userId);
  }, [users]);

  const { setNodeRef } = useSortable({
    id: column.id,
    data: {
      type: 'Column',
      column,
    },
  });

  return (
    <div ref={setNodeRef} className="w-full rounded-lg bg-slate-50 p-2">
      {/* Column title */}
      <h3 className="rounded-sm bg-slate-100 p-2 select-none">
        <span className="rounded-md bg-slate-200 px-2 py-1">{users?.length}</span>{' '}
        {column.title}
      </h3>

      {/* Column basic container */}
      <ScrollArea className="mt-2 flex grow flex-col gap-4">
        <SortableContext strategy={verticalListSortingStrategy} items={usersIds}>
          <div className="flex h-[400px] flex-col gap-2">
            {users.map((user) => (
              <UserCard key={user.userId} user={user} />
            ))}
          </div>
        </SortableContext>
      </ScrollArea>
    </div>
  );
}

export function PlantUserAccessManagementDesktop({
  users,
  setUsers,
  isLoading,
}: {
  users: TUserWithAccessControl[];
  setUsers: React.Dispatch<React.SetStateAction<TUserWithAccessControl[]>>;
  isLoading: boolean;
}) {
  const [columns, setColumns] = useState<TColumn[]>(defaultCols);
  const columnsId = useMemo(() => columns.map((col) => col.id), [columns]);

  const [activeUser, setActiveUser] = useState<TUserWithAccessControl | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  if (isLoading)
    return (
      <div className="grid h-full max-h-[470px] w-full grid-cols-2 gap-4">
        <Skeleton />
        <Skeleton />
      </div>
    );

  return (
    <div className="flex w-full overflow-x-auto overflow-y-hidden">
      <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <SortableContext items={columnsId}>
          <div className="grid flex-1 grid-cols-2 gap-4">
            {columns.map((col) => (
              <ColumnContainer
                key={col.id}
                column={col}
                users={users.filter((user) => user.columnId === col.id)}
              />
            ))}
          </div>
        </SortableContext>

        {createPortal(
          <DragOverlay>{activeUser && <UserCard user={activeUser} />}</DragOverlay>,
          document.body
        )}
      </DndContext>
    </div>
  );

  function onDragStart(event: DragStartEvent) {
    if (event.active.data.current?.type === 'User') {
      setActiveUser(event.active.data.current.user);
      return;
    }
  }

  function onDragEnd(event: DragEndEvent) {
    setActiveUser(null);

    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    const isActiveAColumn = active.data.current?.type === 'Column';
    if (isActiveAColumn) {
      setColumns((columns) => {
        const activeColumnIndex = columns.findIndex((col) => col.id === activeId);
        const overColumnIndex = columns.findIndex((col) => col.id === overId);

        return arrayMove(columns, activeColumnIndex, overColumnIndex);
      });
      return;
    }

    const isActiveAUser = active.data.current?.type === 'User';
    const isOverAUser = over.data.current?.type === 'User';

    if (!isActiveAUser) return;

    if (isActiveAUser && isOverAUser) {
      setUsers((users) => {
        const activeIndex = users.findIndex((t) => t.userId === activeId);
        const overIndex = users.findIndex((t) => t.userId === overId);

        if (users[activeIndex].columnId != users[overIndex].columnId) {
          users[activeIndex].columnId = users[overIndex].columnId;
          users[activeIndex].newlyAdded = true;

          return arrayMove(users, activeIndex, overIndex - 1);
        }

        return arrayMove(users, activeIndex, overIndex);
      });
    }

    const isOverAColumn = over.data.current?.type === 'Column';

    if (isActiveAUser && isOverAColumn) {
      setUsers((users) => {
        const activeIndex = users.findIndex((t) => t.userId === activeId);

        users[activeIndex].columnId = overId;
        users[activeIndex].newlyAdded = true;

        return arrayMove(users, activeIndex, activeIndex);
      });
    }
  }
}
